import styled, { createGlobalStyle, css, FlattenSimpleInterpolation } from 'styled-components';

/* Locks the ns-resize cursor for the whole resize drag — it would otherwise resolve
   per-element-under-pointer. The 🚫 badge is rendered by RobotScheduleEventContent. */
export const GlobalResizeLockedCursorStyle = createGlobalStyle`
  body.robot-schedule__resize-locked-cursor,
  body.robot-schedule__resize-locked-cursor * {
    cursor: ns-resize !important;
  }
`;

export const StyledRobotSchedule = styled.div`
  flex: 1;
  min-height: 400px;
  max-height: 100%;
  overflow: hidden;

  /* antd's Spin wrapper divs don't inherit height by default — without this, the height
     chain that .robot-schedule__calendar needs for its internal scroll area breaks. */
  .robot-schedule__spin,
  .robot-schedule__spin .ant-spin-nested-loading,
  .robot-schedule__spin .ant-spin-container {
    height: 100%;
  }

  .robot-schedule__calendar {
    height: 100%;

    .rbc-header {
      display: flex;
      justify-content: flex-start;
      border: 0;
      padding: 0;
    }

    .rbc-time-view,
    .rbc-events-container,
    .rbc-time-header,
    .rbc-time-header-content {
      border: 0;
    }

    /* Hide the scrollbar so it never shrinks the body's width relative to the header
       above it (e.g. macOS "Always show scroll bars"); still scrollable via wheel/trackpad. */
    .rbc-time-content {
      -ms-overflow-style: none;
      scrollbar-width: none;

      &::-webkit-scrollbar {
        display: none;
      }
    }

    /* Cancel RBC's own scrollbar-compensation margin on the header — no longer needed
       now that the scrollbar above is hidden. */
    .rbc-time-header.rbc-overflowing {
      margin-right: 0 !important;
    }

    /* Remove half-hour lines; keep only full-hour top border */
    .rbc-day-slot .rbc-time-slot {
      border-top: 0;
    }

    /* Remove react-big-calendar default 2px top border on content area */
    .rbc-time-content {
      border-top: 0;
    }

    /* Remove default timeslot-group bottom border; re-applied via token rule */
    .rbc-timeslot-group {
      border-bottom: 0;
    }

    .rbc-time-header-cell {
      height: 56px;
    }

    .rbc-allday-cell {
      display: none;
    }

    .rbc-today {
      background-color: transparent;
    }

    .rbc-events-container {
      margin-right: 4px;

      .rbc-addons-dnd-resize-ns-anchor {
        color: transparent;
        cursor: ns-resize;
      }

      /* Move the bottom resize handle up to the cleaning/charging seam — the fraction is set
         per event by eventPropGetter in RobotSchedule.tsx. */
      .rbc-addons-dnd-resize-ns-anchor:last-child {
        bottom: calc(var(--robot-schedule-charging-fraction, 0) * 100%);
      }

      .rbc-event {
        outline: none;
      }
    }

    .rbc-timeslot-group {
      min-height: 64px;
    }

    .rbc-day-slot .rbc-event,
    .rbc-day-slot .rbc-background-event {
      padding: 0;
      border: none;
      background-color: transparent;
      box-shadow: none;
      min-height: 22px;
      border-radius: 0;
    }

    /* Hide redundant time label — our event component renders its own time */
    .rbc-event-label {
      display: none;
    }
  }

  ${({ theme: { kcc } }): FlattenSimpleInterpolation => css`
    .robot-schedule__calendar {
      /* 12px tick at each hour boundary in the time gutter */
      .rbc-time-gutter {
        overflow: visible;
      }

      .rbc-time-gutter .rbc-timeslot-group {
        position: relative;

        &::after {
          content: '';
          position: absolute;
          right: 0;
          top: 0;
          width: 12px;
          height: 2px;
          background-color: ${kcc.colors.BORDER};
        }
      }

      /* Full-width horizontal line at the top of each hour in day columns.
         border-top aligns with the ::after tick (both at top: 0 of each group) */
      .rbc-day-slot .rbc-timeslot-group {
        border-top: 2px solid ${kcc.colors.BORDER};
      }

      /* Vertical column separators in the time content area */
      .rbc-time-content > * + * > * {
        border-left: 2px solid ${kcc.colors.BORDER};
      }

      /* Right border of Sun in body */
      .rbc-time-content > *:last-child > * {
        border-right: 2px solid ${kcc.colors.BORDER};
      }

      /* Header column separators via ::before/::after (NOT border-left/right).
         border-left on flex items forces content-space distribution, making the last
         column absorb rounding remainder (+2px). Pseudo-elements are outside the
         flex box model → uniform column widths → aligned with body columns. */
      .rbc-time-header-content .rbc-header {
        position: relative;
      }

      .rbc-time-header-content .rbc-header:first-child::before,
      .rbc-time-header-content .rbc-header + .rbc-header::before {
        content: '';
        position: absolute;
        left: 0;
        top: 0;
        bottom: 0;
        width: 2px;
        background-color: ${kcc.colors.BORDER};
      }

      .rbc-time-header-content .rbc-header:last-child::after {
        content: '';
        position: absolute;
        right: 0;
        top: 0;
        bottom: 0;
        width: 2px;
        background-color: ${kcc.colors.BORDER};
      }

      .rbc-time-gutter .rbc-label {
        font-family: ${kcc.typography.FONT_FAMILY};
        font-size: ${kcc.typography.FONT_SIZE.BASE}px;
        color: ${kcc.colors.TEXT};
        padding: 0 ${kcc.spacing.SM};
      }

      .rbc-current-time-indicator {
        height: 2px;
        background-color: ${kcc.colors.PETROL[400]};

        &::before {
          content: '';
          position: absolute;
          left: -4px;
          top: 50%;
          transform: translateY(-50%);
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background-color: ${kcc.colors.PETROL[400]};
        }
      }
    }
  `}
`;
